/**
 * "Returned to you": the work another person sent back to you, from
 * GET /send-backs/mine?direction=received&open=true. Each row says who
 * returned it, when, why, and links to the record.
 *
 * A failed read is not an empty list. The hook reports `status`, and the list
 * says "could not load" for a failure and nothing at all when the route is not
 * there (the send-back gate is closed on that server).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReturnedRow, SendBackFlow, sendBackApi } from './sendBackApi';
import './sendBack.css';

const ZONE = 'America/New_York';
const when = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: ZONE });
};

export const FLOW_LABEL: Record<SendBackFlow, string> = {
  PA_TO_PA: 'Returned by the previous Patient Advocate',
  UMED_TO_PA: 'Returned by UMED',
  FINANCE_TO_UMED: 'Returned by Finance',
  MANIFEST_TO_PROVIDER: 'Returned for a retest',
};

export type ReturnedState =
  | { status: 'loading' }
  | { status: 'absent' }                  // 404: the send-back routes are not registered on this server
  | { status: 'error'; message: string; httpStatus?: number }   // httpStatus is undefined when the server was never reached
  | { status: 'ready'; rows: ReturnedRow[] };

/** The caller's open returned work. `reload` re-reads after an act that closes one. */
export function useReturned(flow?: SendBackFlow): { state: ReturnedState; reload: () => Promise<void> } {
  const [state, setState] = useState<ReturnedState>({ status: 'loading' });
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const load = useCallback(async () => {
    const r = await sendBackApi.mine({ direction: 'received', open: true, flow, limit: 50 });
    if (!live.current) return;
    if (r.success && r.data && Array.isArray(r.data.rows)) { setState({ status: 'ready', rows: r.data.rows }); return; }
    if (r.status === 404) { setState({ status: 'absent' }); return; }
    setState({ status: 'error', message: r.message || r.error || 'Could not load what was returned to you.', httpStatus: r.status });
  }, [flow]);
  useEffect(() => { load(); }, [load]);
  return { state, reload: load };
}

/** The number to show on a tab or heading; null while unknown or when there is nothing to count. */
/**
 * Whether a workspace should offer the Returned-to-you tab: when the list
 * loaded, or when the server answered with a failure (so the failure is said
 * rather than the tab vanishing). Not while loading, not when the routes are
 * absent, and not when the server was never reached (the page is failing as a
 * whole then, and a second alert would only repeat it).
 */
export const offersReturnedTab = (s: ReturnedState): boolean => s.status === 'ready' || (s.status === 'error' && s.httpStatus !== undefined);

export const returnedCount = (s: ReturnedState): number | null => (s.status === 'ready' ? s.rows.length : null);

export function ReturnedCount({ state }: { state: ReturnedState }) {
  const n = returnedCount(state);
  if (n === null) return null;
  return <span className="sb-badge" aria-label={`${n} returned to you`}>{n}</span>;
}

export default function ReturnedToYou({ state, heading = 'Returned to you', empty = 'Nothing has been returned to you.' }: {
  state: ReturnedState; heading?: string; empty?: string;
}) {
  if (state.status === 'absent') return null;
  return (
    <section className="sb-returned" aria-label={heading}>
      <h2>{heading} <ReturnedCount state={state} /></h2>
      {state.status === 'loading' && <p role="status" className="sb-meta">Loading…</p>}
      {state.status === 'error' && <p role="alert" className="sb-alert">{state.message}</p>}
      {state.status === 'ready' && state.rows.length === 0 && <p className="sb-meta">{empty}</p>}
      {state.status === 'ready' && state.rows.length > 0 && (
        <ul>
          {state.rows.map((r) => (
            <li key={r.sendBackId}>
              <span><strong>{r.fromName}</strong> returned this to you · <time dateTime={r.signedAt}>{when(r.signedAt)}</time></span>
              <span className="sb-reason">{r.reason}</span>
              <span className="sb-meta">{FLOW_LABEL[r.flow] || r.flow}{r.fromStep ? ` · was at ${r.fromStep}` : ''}</span>
              {r.link && <Link to={r.link}>Open the record</Link>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
